import process from 'process';

import React, { useEffect } from 'react';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { CommunityContextProvider, DeploymentContextProvider } from 'contexts';
import { appWithTranslation } from 'next-i18next/pages';
import { Provider } from 'react-redux';

import { setupGlobalClientErrorHandlers } from '@highlight-cp/metrics/client';
import { handleSDKKey } from '@highlight-cp/feature-flags';
import { createLoggers } from '@highlight-cp/logger';
import { AppTypeProvider } from '@highlight-cp/redux-state';
import { initAmplitude, setAmplitudeUser } from '@highlight-cp/user-analytics';
import { getDefaultCommunity } from '@highlight-cp/hooks';
import { useGetUser } from 'features/account/use-get-user';
import { AppErrorBoundary } from 'features/app/app-error-boundary';
import {
  ClientErrorReportingProvider,
  SyncClientErrorUserId,
} from 'features/app/client-error-reporting';
import { AuthGuard } from 'features/app/auth-guard';
import { HeimdallSessionProvider } from 'features/app/heimdall-session';
import { MetaInfo } from 'features/app/meta-info';
import { reduxWrapper } from 'store';

import '@mantine/core/styles.layer.css';
import '@mantine/carousel/styles.layer.css';
import '@mantine/dates/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
import 'features/app/styles/variables.css';

import { DEFAULT_COMMUNITY_SLUG } from '@highlight-cp/constants';

const { debug, error } = createLoggers({ tag: 'App' });

const GoogleTagManagerClient = dynamic(
  () => import('@next/third-parties/google').then((mod) => mod.GoogleTagManager),
  { ssr: false },
);

/**
 * ### AmplitudeSetup
 *
 * @description
 * Sets the Amplitude user identity when account data is available,
 * but skips user identification when the user is opted-out via GPC.
 */
function AmplitudeSetup({ isGpcOptOut }: { isGpcOptOut: boolean }) {
  const { account } = useGetUser();

  useEffect(() => {
    if (isGpcOptOut) {
      return;
    }

    if (account?.id) {
      setAmplitudeUser({
        userId: account.id,
        communitySlug: account.communitySlug ?? DEFAULT_COMMUNITY_SLUG,
        emailAddress: account.email ?? '',
        orgName: account.communitySlug ?? '',
        orgId: account.communitySlug ?? '',
        firstName: account.firstName ?? undefined,
        lastName: account.lastName ?? undefined,
      });
    }
  }, [account?.id, account, isGpcOptOut]);

  return null;
}

const Layout = dynamic(() => import('features/app/layout'));

const ConfigCatProvider = dynamic(
  () => import('configcat-react').then((mod) => mod.ConfigCatProvider),
  {
    ssr: false,
  },
);

// development env
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  /* eslint-disable  */
  const ReactDOM = require('react-dom');

  const axe = require('@axe-core/react');
  axe(React, ReactDOM, 1000);
}

/**
 * ### _App
 *
 * @description
 * Root application wrapper responsible for wiring layout/auth, and initializing
 * tracking integrations (Amplitude + GTM) when the user is not opted out via GPC.
 *
 * @param props - Next.js app props
 * @returns Next.js root app
 */
function _App({ Component, ...rest }: AppProps) {
  const { store, props } = reduxWrapper.useWrappedStore(rest);
  const {
    pageProps: {
      community,
      configCatSdkKey: _configCatSdkKey,
      amplitudeSdkKey,
      amplitudeServerZone,
      amplitudeSessionReplaySampleRate = 0,
      gpcOptOut,
      heimdallBaseUrl,
      isEuHighlighterHost = false,
      referralCodeValidation,
    },
  } = rest;
  const configCatSdkKey = handleSDKKey(_configCatSdkKey);

  const isGpcOptOutClient =
    typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true;
  const isGpcOptOut = gpcOptOut === true || isGpcOptOutClient === true;

  // Set an explicit opt-out flag so analytics calls made immediately after mount
  // are safely suppressed even if `navigator.globalPrivacyControl` isn't available.
  if (typeof window !== 'undefined') {
    (window as any).__HIGHLIGHT_GPC_OPTOUT__ = isGpcOptOut;
  }

  useEffect(() => {
    return setupGlobalClientErrorHandlers();
  }, []);

  useEffect(() => {
    if (isGpcOptOut) {
      return;
    }

    if (typeof window !== 'undefined' && amplitudeSdkKey) {
      initAmplitude({
        sdkKey: amplitudeSdkKey,
        serverZone: amplitudeServerZone,
        autocapture: true,
        pageViews: true,
        sessionReplaySampleRate: amplitudeSessionReplaySampleRate,
      });
    }
  }, [amplitudeSdkKey, amplitudeServerZone, amplitudeSessionReplaySampleRate, isGpcOptOut]);

  // Keep default Highlight theming (no override) even when SSR fetches the highlight
  // community object so app.letshighlight.com stays visually identical to pre-fetch behavior.
  const branding = community?.branding;
  const communitySlugForTheme = (branding?.slug ?? community?.slug ?? '').trim().toLowerCase();
  const communityThemeOverride =
    branding &&
    communitySlugForTheme &&
    communitySlugForTheme !== DEFAULT_COMMUNITY_SLUG.toLowerCase()
      ? {
          primary: branding.primaryColorHex,
          slug: branding.slug?.trim() || communitySlugForTheme,
          privacyPolicyUrl: branding.privacyPolicyUrl,
        }
      : null;

  return (
    <AppErrorBoundary>
      <ConfigCatProvider
        sdkKey={configCatSdkKey}
        options={{
          setupHooks: (hooks) => {
            hooks.on('clientReady', () => {
              debug('ConfigCat Client ready');
            });

            hooks.on('clientError', (err) => {
              error('ConfigCat Client error', { err });
            });
          },
        }}
      >
        <HeimdallSessionProvider baseURL={heimdallBaseUrl}>
          <Provider store={store}>
            <SyncClientErrorUserId />
            <AmplitudeSetup isGpcOptOut={isGpcOptOut} />
            <ClientErrorReportingProvider>
              <AppTypeProvider.Highlighter>
                <Layout
                  communityThemeOverride={communityThemeOverride}
                  isExternalReferral={referralCodeValidation?.isExternal === true}
                >
                  <MetaInfo />
                  <AuthGuard>
                    <DeploymentContextProvider isEuHighlighterHost={isEuHighlighterHost}>
                      <CommunityContextProvider
                        community={community}
                        fallbackCommunity={getDefaultCommunity(isEuHighlighterHost)}
                      >
                        <Component {...props.pageProps} />
                        {!isGpcOptOut && <GoogleTagManagerClient gtmId="GTM-M2VC8JB" />}
                      </CommunityContextProvider>
                    </DeploymentContextProvider>
                  </AuthGuard>
                </Layout>
              </AppTypeProvider.Highlighter>
            </ClientErrorReportingProvider>
          </Provider>
        </HeimdallSessionProvider>
      </ConfigCatProvider>
    </AppErrorBoundary>
  );
}

export default appWithTranslation(_App);
